import { useCallback, useState } from 'react'
import useExpertiseStore from '@/stores/useExpertiseStore'
import * as geoApi from '@/api/geo'
import * as dataApi from '@/api/data'
import { anneeToEpoque } from '@/lib/utils'

const API_LABELS = {
  cadastre:       'Données cadastrales',
  reglementation: 'Réglementation et urbanisme',
  situation:      'Analyse de situation',
  dvf:            'Données de marché (DVF)',
  cartes:         "Cartes d'urbanisme",
  altimetrie:     'Altimétrie (pente, altitude, orientation)',
  desserte:       'Desserte et transports',
  dpe:            'Performance énergétique (DPE)',
  bpe:            'Équipements et commerces',
  parcelle_data:  'Données parcellaires',
  loyers:         'Loyers de référence',
}

export default function useAutoFill() {
  const { updateField, expertise } = useExpertiseStore()
  const [warnings, setWarnings] = useState([])

  const clearWarnings = useCallback(() => setWarnings([]), [])

  const onAddressSelected = useCallback(async (address) => {
    const { latitude, longitude, code_insee, code_postal, commune, departement } = address
    setWarnings([])

    // Remplissage immédiat
    if (address.adresse) updateField('parcelles.0.adresse', address.adresse)
    if (code_postal) updateField('parcelles.0.code_postal', code_postal)
    if (commune) updateField('parcelles.0.commune', commune)
    if (departement) updateField('parcelles.0.departement', departement)
    if (latitude) updateField('parcelles.0.latitude', latitude)
    if (longitude) updateField('parcelles.0.longitude', longitude)
    if (code_insee) updateField('parcelles.0.code_insee', code_insee)

    // Determine type_bien for DVF (pas de fallback : on attend que l'utilisateur l'ait saisi)
    const typeBien = expertise?.parcelles?.[0]?.biens?.[0]?.type_bien

    // --- Batch 1 : Geo APIs (4-5 appels parallèles, DVF uniquement si type_bien renseigné) ---
    const geoRequests = [
      geoApi.lookupParcelle({ lat: latitude, lon: longitude }),
      geoApi.analyseReglementation(latitude, longitude, code_insee),
      geoApi.analyseSituation(latitude, longitude),
    ]
    // DVF uniquement si type_bien est renseigné
    const dvfIndex = typeBien ? geoRequests.length : -1
    if (typeBien) {
      geoRequests.push(geoApi.rechercheDVF({ code_insee, type_bien: typeBien, lat: latitude, lon: longitude }))
    }
    const cartesIndex = geoRequests.length
    geoRequests.push(geoApi.generateCartesUrbanisme(latitude, longitude))

    const geoResults = await Promise.allSettled(geoRequests)
    const [cadastre, reglementation, situation] = geoResults
    const dvf = dvfIndex >= 0 ? geoResults[dvfIndex] : null
    const cartes = geoResults[cartesIndex]

    // Traiter cadastre
    let parcelleId = null
    if (cadastre.status === 'fulfilled' && cadastre.value) {
      const data = cadastre.value
      if (data.parcelles?.length > 0) {
        updateField('parcelles.0.cadastre_section', data.parcelles[0].section)
        updateField('parcelles.0.cadastre_numero', data.parcelles[0].numero)
        updateField('parcelles.0.contenance_m2', data.parcelles[0].contenance)
        parcelleId = data.parcelles[0].id
      }
      if (data.designation_cadastre) {
        updateField('parcelles.0.situation.designation_cadastre', data.designation_cadastre)
      }
    }

    // Traiter réglementation
    if (reglementation.status === 'fulfilled' && reglementation.value) {
      const data = reglementation.value
      if (data.urbanisme_texte) updateField('parcelles.0.situation.urbanisme_texte', data.urbanisme_texte)
      if (data.risques_texte) updateField('parcelles.0.situation.risques_texte', data.risques_texte)
      if (data.risques_rapport) updateField('parcelles.0.situation.risques_rapport', data.risques_rapport)
      if (data.gpu) updateField('parcelles.0.situation.gpu_data', data.gpu)
    }

    // Traiter situation
    if (situation.status === 'fulfilled' && situation.value) {
      if (situation.value.localisation_texte) {
        updateField('parcelles.0.situation.localisation_texte', situation.value.localisation_texte)
      }
    }

    // Traiter DVF (peut être null si type_bien pas encore renseigné)
    if (dvf?.status === 'fulfilled' && dvf.value) {
      if (dvf.value.analyse_marche_texte) {
        updateField('parcelles.0.situation.analyse_marche_texte', dvf.value.analyse_marche_texte)
      }
      if (dvf.value.stats) {
        updateField('parcelles.0.situation.dvf_stats', dvf.value.stats)
      }
    }

    // Traiter cartes urbanisme
    if (cartes.status === 'fulfilled' && cartes.value?.cartes) {
      updateField('parcelles.0.situation.cartes_urbanisme', cartes.value.cartes)
    }

    // Collecter les échecs Batch 1
    const failed = []
    const geoKeys = ['cadastre', 'reglementation', 'situation']
    if (typeBien) geoKeys.splice(dvfIndex, 0, 'dvf')
    geoKeys.push('cartes')
    geoResults.forEach((r, i) => {
      if (r.status === 'rejected') {
        failed.push({ key: geoKeys[i], label: API_LABELS[geoKeys[i]] })
      }
    })

    // --- Altimétrie : nécessite la géométrie du polygone parcellaire ---
    // Récupérée via reverseParcelle (coord → GeoJSON). Override arbitré
    // dans la spec 2026-04-19 : relief/exposition sont toujours écrasés
    // par les valeurs calculées (relief_classifie, geodesie_classifiee)
    // sauf si elles sont null (edge case pente très faible).
    if (cadastre.status === 'fulfilled' && cadastre.value?.parcelles?.length > 0) {
      try {
        const reverseResp = await geoApi.reverseParcelle(latitude, longitude)
        if (reverseResp?.geometry) {
          const alti = await geoApi.calculerAltimetrie(reverseResp.geometry, 100)
          updateField('parcelles.0.altitude_moyenne_m', alti.altitude_moyenne_m)
          updateField('parcelles.0.altitude_min_m', alti.altitude_min_m)
          updateField('parcelles.0.altitude_max_m', alti.altitude_max_m)
          updateField('parcelles.0.denivele_m', alti.denivele_m)
          updateField('parcelles.0.pente_pct', alti.pente_pct)
          updateField('parcelles.0.pente_pct_max', alti.pente_pct_max)
          updateField('parcelles.0.orientation_azimut_deg', alti.orientation_azimut_deg)
          updateField('parcelles.0.situation.altimetrie_rapport', alti.rapport)
          // Override (arbitrage 2026-04-19) : écrase la saisie expert
          if (alti.relief_classifie != null) {
            updateField('parcelles.0.biens.0.terrain.relief', alti.relief_classifie)
          }
          if (alti.geodesie_classifiee != null) {
            updateField('parcelles.0.biens.0.terrain.exposition', alti.geodesie_classifiee)
          }
        }
      } catch {
        failed.push({ key: 'altimetrie', label: API_LABELS.altimetrie })
      }
    }

    // --- Batch 2 : Carex Data APIs (parcelle + localisation) ---
    if (!latitude || !longitude) {
      if (failed.length > 0) setWarnings(failed)
      return
    }

    const carexRequests = [
      dataApi.getDesserteNear(longitude, latitude),
      dataApi.getDpeNear(longitude, latitude, 200, 10),
      dataApi.getBpeCommuneDensity(code_insee),
    ]

    // Ajouter la recherche par parcelle si on a un ID cadastral
    if (parcelleId) {
      carexRequests.push(dataApi.getParcelleData(parcelleId))
    }

    // Ajouter les loyers si on a le code INSEE
    if (code_insee) {
      carexRequests.push(dataApi.getLoyersCommune(code_insee))
    }

    const carexResults = await Promise.allSettled(carexRequests)

    let idx = 0
    const desserte = carexResults[idx++]
    const dpeNear = carexResults[idx++]
    const bpeDensity = carexResults[idx++]
    const parcelleData = parcelleId ? carexResults[idx++] : null
    const loyers = code_insee ? carexResults[idx++] : null

    // Traiter desserte transports
    if (desserte.status === 'fulfilled' && desserte.value?.summary) {
      const s = desserte.value.summary
      updateField('parcelles.0.situation.desserte_score', s.desserte_score)
      updateField('parcelles.0.situation.desserte_data', desserte.value)
    }

    // Traiter DPE proches (le plus récent pour la même adresse)
    if (dpeNear.status === 'fulfilled' && dpeNear.value?.length > 0) {
      const dpeList = Array.isArray(dpeNear.value) ? dpeNear.value : dpeNear.value.items || []
      if (dpeList.length > 0) {
        updateField('parcelles.0.situation.dpe_voisinage', dpeList)
      }
    }

    // Traiter densité BPE commune
    if (bpeDensity.status === 'fulfilled' && bpeDensity.value) {
      updateField('parcelles.0.situation.bpe_density', bpeDensity.value)
    }

    // Traiter données parcelle agrégées (BDNB, DVF enrichi, SITADEL, RNIC)
    if (parcelleData?.status === 'fulfilled' && parcelleData.value) {
      const pd = parcelleData.value
      _applyBdnb(pd.bdnb, updateField)
      _applySitadel(pd.sitadel, updateField)
      _applyRnic(pd.rnic, updateField)
      _applyDvfParcelle(pd.dvf, updateField)
    }

    // Traiter loyers de référence
    if (loyers?.status === 'fulfilled' && loyers.value) {
      updateField('parcelles.0.situation.loyers_reference', loyers.value)
    }

    // Collecter les échecs Batch 2
    const carexKeys = ['desserte', 'dpe', 'bpe']
    if (parcelleId) carexKeys.push('parcelle_data')
    if (code_insee) carexKeys.push('loyers')
    carexResults.forEach((r, i) => {
      if (r.status === 'rejected') {
        failed.push({ key: carexKeys[i], label: API_LABELS[carexKeys[i]] })
      }
    })

    if (failed.length > 0) setWarnings(failed)
  }, [updateField, expertise])

  // Enrichissement partiel pour parcelles secondaires (cartes urbanisme uniquement)
  const onParcelleAdded = useCallback(async (index, lat, lon) => {
    if (!lat || !lon) return

    try {
      const cartes = await geoApi.generateCartesUrbanisme(lat, lon)
      if (cartes?.cartes) {
        updateField(`parcelles.${index}.situation.cartes_urbanisme`, cartes.cartes)
      }
    } catch {
      // Échec silencieux — les cartes pourront être régénérées à l'onglet urbanisme
    }
  }, [updateField])

  return { onAddressSelected, onParcelleAdded, warnings, clearWarnings }
}


// --- Helpers pour mapper les données Carex vers les champs du formulaire ---

function _applyBdnb(bdnb, updateField) {
  if (!bdnb?.batiments?.length) return
  const bat = bdnb.batiments[0]

  // Année de construction (FFO = Fichiers Fonciers)
  if (bat.ffo_bat?.annee_construction) {
    updateField('parcelles.0.biens.0.bati.date_construction_libre', String(bat.ffo_bat.annee_construction))
    const epoque = anneeToEpoque(bat.ffo_bat.annee_construction)
    if (epoque) {
      updateField('parcelles.0.biens.0.bati.epoque_construction', epoque)
    }
  }

  // Nombre de niveaux
  if (bat.ffo_bat?.nb_niveau) {
    updateField('parcelles.0.biens.0.bati.nombre_etages', bat.ffo_bat.nb_niveau)
  }

  // Matériaux
  if (bat.ffo_bat?.mat_mur_txt && bat.ffo_bat.mat_mur_txt !== 'INDETERMINE') {
    updateField('parcelles.0.biens.0.bati.gros_oeuvre', bat.ffo_bat.mat_mur_txt)
  }
  if (bat.ffo_bat?.mat_toit_txt && bat.ffo_bat.mat_toit_txt !== 'INDETERMINE') {
    updateField('parcelles.0.biens.0.bati.toiture_couverture', bat.ffo_bat.mat_toit_txt)
  }

  // Hauteur (BDTOPO)
  if (bat.bdtopo_bat?.hauteur_mean) {
    updateField('parcelles.0.biens.0.bati.hauteur_moyenne', bat.bdtopo_bat.hauteur_mean)
  }

  // Type de bien déduit de l'usage FFO
  if (bat.ffo_bat?.usage_niveau_1_txt) {
    const usageMapping = {
      'Résidentiel individuel': 'maison',
      'Résidentiel collectif': 'appartement',
    }
    const typeBien = usageMapping[bat.ffo_bat.usage_niveau_1_txt]
    if (typeBien) {
      updateField('parcelles.0.biens.0.type_bien', typeBien)
    }
  }

  // DPE intégré au BDNB
  if (bat.dpe_logement) {
    const dpe = bat.dpe_logement
    if (dpe.classe_bilan_dpe) {
      updateField('parcelles.0.biens.0.performance_energetique.dpe_disponible', true)
      updateField('parcelles.0.biens.0.performance_energetique.dpe_impact', `Classe ${dpe.classe_bilan_dpe}`)
    }
    // Surface habitable depuis le DPE
    if (dpe.surface_habitable_logement != null) {
      updateField('parcelles.0.biens.0.surfaces.habitable_m2', dpe.surface_habitable_logement)
    }
  }

  // Stocker les données brutes BDNB pour consultation
  updateField('parcelles.0.situation.bdnb_data', bdnb)
}


function _applySitadel(sitadel, updateField) {
  if (!sitadel || (Array.isArray(sitadel) && sitadel.length === 0)) return

  const items = sitadel.items || sitadel
  if (!Array.isArray(items) || items.length === 0) return

  // Prendre le permis le plus récent
  const sorted = [...items].sort((a, b) =>
    (b.date_autorisation || '').localeCompare(a.date_autorisation || '')
  )
  const pc = sorted[0]

  if (pc.num_dau) {
    updateField('parcelles.0.biens.0.urbanisme_risques.pc_numero', pc.num_dau)
  }
  if (pc.date_autorisation) {
    updateField('parcelles.0.biens.0.urbanisme_risques.pc_date', pc.date_autorisation)
  }
  if (pc.date_daact) {
    updateField('parcelles.0.biens.0.urbanisme_risques.daact_date', pc.date_daact)
  }

  // Stocker l'historique complet des permis
  updateField('parcelles.0.situation.sitadel_data', items)
}


function _applyRnic(rnic, updateField) {
  if (!rnic || (Array.isArray(rnic) && rnic.length === 0)) return

  const items = Array.isArray(rnic) ? rnic : [rnic]
  if (items.length === 0) return

  // Stocker les données copropriété pour les templates LOT
  updateField('parcelles.0.situation.rnic_data', items)
}


function _applyDvfParcelle(dvf, updateField) {
  if (!dvf?.items?.length) return

  // Stocker les transactions DVF enrichies sur la parcelle
  updateField('parcelles.0.situation.dvf_parcelle', dvf.items)
}
